import { Link, useRouterState } from "@tanstack/react-router";
import { detectCluster, getClusterTheme, type Cluster } from "@/lib/cluster";
import { useLocale } from "@/i18n/LocaleContext";
import { todayISO, parseISO, MONTH_SLUGS_ID, MONTH_NAMES_ID } from "@/lib/calendar/dates";
import { currentHijriYear } from "@/lib/calendar/hijri";

type Item = { to: string; label: string };

function calendarItems(href: (p: string) => string, y: number, locale: string): Item[] {
  const cal = locale === "en" ? "Calendar" : "Kalender";
  const months = MONTH_SLUGS_ID.map((s, i) => ({ to: href(`/kalender/${y}/${s}`), label: MONTH_NAMES_ID[i].slice(0, 3) }));
  return [
    { to: href(`/kalender/${y}`), label: `${cal} ${y}` },
    { to: href(`/kalender/${y + 1}`), label: `${cal} ${y + 1}` },
    { to: href(`/tanggal-merah/${y}`), label: locale === "en" ? `Red Dates ${y}` : `Tanggal Merah ${y}` },
    { to: href(`/libur-nasional/${y}`), label: locale === "en" ? `Public Holidays ${y}` : `Libur Nasional ${y}` },
    { to: href(`/cuti-bersama/${y}`), label: locale === "en" ? `Joint Leave ${y}` : `Cuti Bersama ${y}` },
    { to: href(`/hari-ini`), label: locale === "en" ? "Today" : "Hari Ini" },
    ...months,
  ];
}

function hijriItems(href: (p: string) => string, hy: number, y: number): Item[] {
  const popular = [
    { to: href(`/kalender-hijriah/${hy}/9/`), label: "Ramadan" },
    { to: href(`/kalender-hijriah/${hy}/10/`), label: "Syawal" },
    { to: href(`/kalender-hijriah/${hy}/12/`), label: "Dzulhijjah" },
  ];
  return [
    { to: href(`/kalender-hijriah/${hy}/`), label: `Hijri ${hy}H` },
    { to: href(`/kalender-hijriah/${hy + 1}/`), label: `Hijri ${hy + 1}H` },
    ...popular,
    { to: `/jadwal-imsak/`, label: "Jadwal Imsakiyah" },
    { to: `/jadwal-buka-puasa/`, label: "Buka Puasa" },
    { to: `/jadwal-imsak/lokasi/`, label: "Lokasi Imsak" },
    { to: `/harpitnas/${y}/`, label: `Harpitnas ${y}` },
  ];
}

function prayerItems(): Item[] {
  return [
    { to: "/jadwal-sholat/", label: "Hari Ini" },
    { to: "/jadwal-sholat/lokasi/", label: "Indeks Lokasi" },
    { to: "/jadwal-sholat/dki-jakarta", label: "Jakarta" },
    { to: "/jadwal-sholat/bandung", label: "Bandung" },
    { to: "/jadwal-sholat/surabaya", label: "Surabaya" },
    { to: "/jadwal-sholat/medan", label: "Medan" },
    { to: "/jadwal-sholat/yogyakarta", label: "Yogyakarta" },
    { to: "/jadwal-sholat/semarang", label: "Semarang" },
    { to: "/jadwal-sholat/makassar", label: "Makassar" },
    { to: "/jadwal-sholat/denpasar", label: "Denpasar" },
    { to: "/jadwal-sholat/bekasi", label: "Bekasi" },
    { to: "/jadwal-sholat/tangerang", label: "Tangerang" },
    { to: "/jadwal-imsak/", label: "Imsakiyah" },
    { to: "/jadwal-buka-puasa/", label: "Buka Puasa" },
  ];
}

function toolsItems(href: (p: string) => string): Item[] {
  return [
    { to: href("/tools"), label: "Semua Tools" },
    { to: href("/tools/kalkulator-hari"), label: "Kalkulator Hari" },
    { to: href("/tools/cek-weton"), label: "Cek Weton" },
    { to: href("/tools/hari-jadian"), label: "Hari Jadian" },
    { to: href("/tools/hari-lahir"), label: "Hari Lahir" },
    { to: href("/tools/hari-menikah"), label: "Lama Menikah" },
    { to: href("/tools/hari-ldr"), label: "LDR" },
    { to: href("/tools/hari-putus"), label: "Hari Putus" },
    { to: href("/tools/hari-sahabat"), label: "Sahabat" },
    { to: "/zodiak/", label: "Zodiak" },
    { to: "/hari-ini", label: "Hari Ini" },
  ];
}

function importantItems(href: (p: string) => string, y: number): Item[] {
  const zodiacs = ["aries","taurus","gemini","cancer","leo","virgo","libra","scorpio","sagittarius","capricorn","aquarius","pisces"];
  return [
    { to: href("/hari"), label: "Semua Hari Penting" },
    { to: `/harpitnas/${y}/`, label: `Harpitnas ${y}` },
    { to: `/harpitnas/${y + 1}/`, label: `Harpitnas ${y + 1}` },
    { to: "/zodiak/", label: "Zodiak" },
    { to: "/zodiak/cocok/", label: "Kecocokan Zodiak" },
    ...zodiacs.map((s) => ({ to: `/zodiak/${s}/`, label: s.charAt(0).toUpperCase() + s.slice(1) })),
  ];
}

export function ClusterNav() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const cluster = detectCluster(pathname);
  const { href, locale } = useLocale();
  if (!cluster) return null;
  const theme = getClusterTheme(cluster);
  const today = parseISO(todayISO())!;
  const y = today.y;
  const hy = currentHijriYear();
  let items: Item[] = [];
  switch (cluster) {
    case "calendar": items = calendarItems(href, y, locale); break;
    case "hijri": items = hijriItems(href, hy, y); break;
    case "prayer": items = prayerItems(); break;
    case "tools": items = toolsItems(href); break;
    case "important": items = importantItems(href, y); break;
  }
  const label = theme.label[locale === "en" ? "en" : "id"];
  return (
    <nav
      aria-label={label}
      className={`border-b ${theme.accentBorder} bg-muted/30`}
    >
      <div className="container mx-auto flex items-center gap-2 overflow-x-auto px-3 py-2 text-xs sm:px-4">
        <span className={`shrink-0 font-semibold uppercase tracking-wide ${theme.accentText}`}>{label}:</span>
        {items.map((it) => {
          const active = pathname === it.to || pathname === it.to.replace(/\/$/, "");
          return (
            <Link
              key={it.to}
              to={it.to as never}
              className={
                "shrink-0 rounded-md px-2 py-1 transition hover:bg-accent " +
                (active ? `${theme.accentBg} font-semibold ${theme.accentText}` : "text-muted-foreground")
              }
            >
              {it.label}
            </Link>
          );
        })}
      </div>
    </nav>
  );
}